/**
 * @fileoverview Shared sensitive query-param detection (edge + client safe).
 * @description Param keys that may carry secrets, one-time tokens, or PII must not
 * be emitted to GA4 or cached on shared CDNs when present on redirect targets.
 *
 * @module lib/analytics/sensitive-query-params
 */

/**
 * Query-param names that may carry secrets/one-time tokens/PII.
 * Matched case-insensitively against each param key (word-boundaried for
 * short keys so `sort_key`/`mode` etc. aren't matched).
 */
export const SENSITIVE_QUERY_PARAM_RE =
  /(session|token|secret|password|pwd|api[_-]?key|\bkey\b|email|\botp\b|signature|\bsig\b|\bcode\b|\bauth\b)/i

/**
 * Whether a URL search string contains sensitive query parameters.
 *
 * @param {string} search - Search from `URL.search` (with or without leading `?`).
 * @returns {boolean} True when a sensitive param key is present.
 */
export function hasSensitiveQueryParams(search: string): boolean {
  if (!search) return false
  const raw = search.startsWith('?') ? search.slice(1) : search
  if (!raw) return false
  const params = new URLSearchParams(raw)
  for (const key of params.keys()) {
    if (SENSITIVE_QUERY_PARAM_RE.test(key)) return true
  }
  return false
}
